import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { UserService } from '@app/services/user/user.service';
import { Observable, of } from 'rxjs';
import { concatMap, filter, map, switchMap, take } from 'rxjs/operators';
import { AccountService } from '@core/services/account/account.service';
import { OnboardingProductsEnum } from '@core/services/account/account.interface';
import { AppRouteHelper } from '@shared/helpers/app-route.helper';
import { isEmptyObject } from '@app/utils/is-empty-helper';

@Injectable({
	providedIn: 'root',
})
export class EmptyAccountsGuard implements CanActivate {
	constructor(
		private userService: UserService,
		private featureFlagManager: FeatureFlagManagerService,
		private router: Router,
		private accountService: AccountService,
	) {}

	public canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<UrlTree | boolean> {
		const isAfterLogin = _.queryParamMap.has('code');

		if (!isAfterLogin) return of(true);

		return this.findDefaultRoute();
	}

	private findDefaultRoute(): Observable<UrlTree | boolean> {
		return this.userService.isAuthenticated$.pipe(
			take(1),
			concatMap((isAuthenticated: boolean) => {
				if (!isAuthenticated) {
					return this.userService.requestLogin().pipe(map(() => true));
				}

				return this.userService.user$.pipe(
					take(1),
					switchMap(() => this.accountService.loadAccounts()),
					switchMap(() =>
						this.featureFlagManager.flags$.pipe(
							filter((flags) => !isEmptyObject(flags)),
							take(1),
						),
					),
					map(() => {
						const accounts = this.accountService.getAccountsSnapshot();
						// if empty then navigate to onboarding
						const hasAccounts = accounts.length > 0;
						if (!hasAccounts) {
							return this.router.parseUrl(`/${AppRouteHelper.onboardAccount}`);
						}
						// if some account has Kompass enabled then skip home and navigate directly to Kompass
						const isKompassEnabled = accounts.some((account) =>
							account.product_permissions?.includes(OnboardingProductsEnum.Kompass),
						);
						if (isKompassEnabled) {
							return this.router.parseUrl(`/${AppRouteHelper.kompass}`);
						}

						return true;
					}),
				);
			}),
		);
	}
}
